import React, { useState, useContext } from 'react'
import { Box } from '@mui/material'

import MusicNoteIcon from '@mui/icons-material/MusicNote'
import PianoIcon from '@mui/icons-material/Piano'
import { Instrument, LanguageCode, Song, SONG_TYPES } from '../../../../utils/types'
import BasicButton from '../../../../assets/components/BasicButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { buttonSounds, hybridSounds, joystickSounds, touchSounds } from '../../../../utils/api'
import { Drawer } from '../../Welcome'
import AllSoundsAvailable from '../../Components/AllSoundsAvailable'
import InstrumentSettings from '../../Components/InstrumentSettings'
import { PolyglotContext } from '../../../../polyglot/polyGlotContext'
import { formatKeytone, getSongTitle } from '../../../../utils/songHelpes'
import { useCurrentUser } from '../../../../hooks/contexts/currentUserContext'

interface InstrumentInformationProps {
    instrument: Instrument
    songList: Song[] | undefined
    shouldShowSettings: boolean
    currentSong: Song | undefined
    isSmallScreen: boolean
}

function getArrangementTitle(instrument: Instrument, songList: Song[] | undefined, lang: LanguageCode = 'sv') {
    const arrangement = songList?.find((song: Song) => instrument.arrangement === song.id)
    const translatedArrangementTitle = arrangement ? getSongTitle(arrangement, lang) : null
    return translatedArrangementTitle || arrangement?.title || instrument.arrangement
}

const InstrumentInformation = ({ currentSong, instrument, songList = [], isSmallScreen }: InstrumentInformationProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const currentUser = useCurrentUser()

    const lang: LanguageCode = currentUser?.customer?.userSettings?.language ?? 'sv'

    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const [isSongsOpen, setIsSongsOpen] = useState(false)

    const currentInstrumentSong = songList.find((s) => s.id === instrument.currentSong?.id)

    const selectedInstrument = {
        deviceId: instrument.deviceId,
        arrangement: instrument.arrangement,
        type: instrument.type,
        serialNr: instrument.serialNr,
        batteryLevel: instrument.batteryLevel,
        volume: instrument.volume
    }

    const openDrawer = (type: 'settings' | 'songs') => {
        if (type === 'settings') setIsSettingsOpen(true)
        else setIsSongsOpen(true)
    }

    const closeDrawer = (type: 'settings' | 'songs') => {
        if (type === 'settings') setIsSettingsOpen(false)
        else setIsSongsOpen(false)
    }

    function getInstrumentSound() {
        if (instrument.currentSong?.sound) {
            if (instrument.type === 'buttons') {
                return buttonSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'joysticks') {
                return joystickSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'touch') {
                return touchSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'hybrid') {
                return hybridSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
        }
        return null
    }

    const instrumentSound = getInstrumentSound()
    const soundName = instrumentSound?.name || 'Unknown sound'

    const title = instrument.protocol === '1.0' ? (currentInstrumentSong ? (currentInstrumentSong.type === SONG_TYPES.KEY ? formatKeytone(currentInstrumentSong.key, lang) : getSongTitle(currentInstrumentSong, lang)) : '') : getArrangementTitle(instrument, songList, lang)

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: isSmallScreen ? 'row' : 'column',
                columnGap: isSmallScreen ? '0.5rem' : 0,
                justifyContent: 'space-between'
            }}
        >
            {/* SONG BUTTON */}

            <Box sx={{ flex: 1, maxWidth: '150px', pt: '0.5rem' }}>
                <BasicButton color="primary" icon={KeyboardArrowDownIcon} title={title ? title : polyglot.t('home.instrumentInformation.headingChooseSound')} variant="outlined" onClick={() => openDrawer('songs')} />

                <Drawer variant="bottom" isOpen={isSettingsOpen} toggleDrawer={() => closeDrawer('settings')} heading={polyglot.t('home.instrumentInformation.headingChooseSound')}>
                    <InstrumentSettings currentSong={currentSong} selectedInstrument={selectedInstrument} />
                </Drawer>

                <Drawer variant="bottom" isOpen={isSongsOpen} toggleDrawer={() => closeDrawer('songs')}>
                    <AllSoundsAvailable songList={songList} selectedInstrument={selectedInstrument} lang={lang} />
                </Drawer>
            </Box>

            {/* SOUND BUTTON */}
            {instrumentSound && (
                <Box pt="0.5rem">
                    <BasicButton icon={KeyboardArrowDownIcon} variant="outlined" color="primary" title={soundName} onClick={() => openDrawer('settings')} />
                </Box>
            )}
        </Box>
    )
}

export default InstrumentInformation
